import {
  type BulkPackTakedownBody,
  type BulkPackTakedownResponse,
  bulkPackTakedownResponseDto,
  type PackDetail,
  type PublishedPack,
  packDetailDto,
  publishedPackDto,
  type TakedownResponse,
  takedownResponseDto,
} from '@planuze/admin-contracts';
import {
  ApiError,
  buildQuery,
  cursorPageSchema,
  mapHttpError,
  parseJson,
  readErrorCode,
  request,
} from './http.js';

/* Cliente REST do catálogo de packs publicados (read-only, ADR-0400). Consome o
   admin-service GET /v1/admin/packs, que lê o catálogo global do pack-registry via
   HMAC. Distinto da moderação (pack-submissions): aqui é o que JÁ está publicado.
   Paginação por cursor keyset (espelha companies-api): `{ items, nextCursor }`. */

interface PacksCatalogQuery {
  readonly cursor?: string | undefined;
}

const packsPageSchema = cursorPageSchema(publishedPackDto);

async function catalogError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  return new ApiError(res.status, 'server_error', code || 'request failed');
}

/** GET /v1/admin/packs — página do catálogo por cursor keyset. */
export async function listPublishedPacks(
  query: PacksCatalogQuery = {},
): Promise<{ readonly items: readonly PublishedPack[]; readonly nextCursor: string | null }> {
  const res = await request(`/v1/admin/packs${buildQuery({ cursor: query.cursor })}`);
  if (!res.ok) throw await catalogError(res);
  const page = await parseJson(res, packsPageSchema);
  return { items: page.items, nextCursor: page.nextCursor ?? null };
}

/* Packages org-private autorizados a uma org (aba Companies, ADR-0400+). Filtro
   server-side por orgId; read-only, sublista completa (não paginada). */
export async function listOrgPackages(orgId: string): Promise<readonly PublishedPack[]> {
  const res = await request(`/v1/admin/packs?orgId=${encodeURIComponent(orgId)}`);
  if (!res.ok) throw await catalogError(res);
  const page = await parseJson(res, packsPageSchema);
  return page.items;
}

/* Detalhe rico de UM pack (Fase 2, ADR-0703+): metadata + versões, via GET /v1/admin/packs/:packId.
   `mapHttpError` distingue 404 (pack removido/inexistente) das demais falhas. */
export async function getPackDetail(packId: string): Promise<PackDetail> {
  const res = await request(`/v1/admin/packs/${encodeURIComponent(packId)}`);
  if (!res.ok) throw await mapHttpError(res);
  return parseJson(res, packDetailDto);
}

/* Takedown de versão por staff (ADR-0464): enforcement por versão. Motivo obrigatório. */
export async function takedownPackVersion(
  packId: string,
  version: string,
  reason: string,
): Promise<TakedownResponse> {
  const res = await request(
    `/v1/admin/packs/${encodeURIComponent(packId)}/versions/${encodeURIComponent(version)}/takedown`,
    { method: 'POST', body: { reason } },
  );
  if (!res.ok) throw await catalogError(res);
  return parseJson(res, takedownResponseDto);
}

/** POST /v1/admin/packs/bulk-takedown — alvos pack+versão congelados; parcial por item. */
export async function bulkTakedownPackVersions(
  body: BulkPackTakedownBody,
): Promise<BulkPackTakedownResponse> {
  const res = await request('/v1/admin/packs/bulk-takedown', { method: 'POST', body });
  if (!res.ok) throw await catalogError(res);
  return parseJson(res, bulkPackTakedownResponseDto);
}
